import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useParams } from 'react-router';
import { getApiBaseUrl } from '../../utils/apiClient';
import {
  STUDIO_COPY,
  STUDIO_FILMSTRIP_BAND_PNG,
  STUDIO_FILMSTRIP_BAND_SRC,
  STUDIO_PRODUCT_NAME,
  STUDIO_PUBLIC_WATCH_LOGO_SRC,
} from '../config';
import { publicReelTitle } from '../utils/reelName';
import PublicWatchHowItWorks from './PublicWatchHowItWorks';
import './publicWatch.css';

type PublicReel = {
  title: string;
  playback_url: string;
  poster_url?: string | null;
  duration_sec?: number | null;
};

const FILM_BAND_STYLE: React.CSSProperties = {
  ['--rtts-film-band-png' as string]: `url("${STUDIO_FILMSTRIP_BAND_PNG}")`,
  ['--rtts-film-band-set' as string]: `image-set(url("${STUDIO_FILMSTRIP_BAND_SRC}") type("image/webp"), url("${STUDIO_FILMSTRIP_BAND_PNG}") type("image/png"))`,
};

const PublicReelWatchPage: React.FC = () => {
  const { token = '' } = useParams();
  const [reel, setReel] = useState<PublicReel | null>(null);
  const [missing, setMissing] = useState(false);

  useLayoutEffect(() => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('href', STUDIO_FILMSTRIP_BAND_SRC);
    link.setAttribute('type', 'image/webp');
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
    return () => {
      link.remove();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await fetch(
          `${getApiBaseUrl()}/api/rtts/public/reel/${encodeURIComponent(token)}`,
          { credentials: 'omit' }
        );
        if (!response.ok) {
          if (!cancelled) setMissing(true);
          return;
        }
        const body = (await response.json()) as PublicReel;
        if (!cancelled) {
          setReel(body);
          const safeTitle = publicReelTitle(body.title);
          document.title = `${safeTitle} — ${STUDIO_PRODUCT_NAME}`;
        }
      } catch {
        if (!cancelled) setMissing(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const title = missing
    ? STUDIO_COPY.publicWatchUnavailable
    : publicReelTitle(reel?.title);

  return (
    <main className="rtts-public-watch" data-testid="rtts-public-watch">
      <a className="rtts-public-brand" href={STUDIO_COPY.publicWatchCtaHref}>
        <img
          className="rtts-public-logo"
          src={STUDIO_PUBLIC_WATCH_LOGO_SRC}
          alt={STUDIO_PRODUCT_NAME}
          data-testid="rtts-public-logo"
        />
      </a>
      <div
        className="rtts-public-film-band"
        data-testid="rtts-public-film-band"
        aria-hidden="true"
        style={FILM_BAND_STYLE}
      />
      <h1 data-testid="rtts-public-title">{title}</h1>
      <div className="rtts-public-frame">
        {missing ? (
          <div
            className="rtts-public-unavailable"
            data-testid="rtts-public-unavailable"
          >
            {STUDIO_COPY.publicWatchUnavailable}
          </div>
        ) : reel?.playback_url ? (
          <video
            className="rtts-public-player"
            controls
            playsInline
            preload="metadata"
            poster={reel.poster_url || undefined}
            src={reel.playback_url}
            data-testid="rtts-public-player"
          />
        ) : (
          <div className="rtts-public-unavailable" aria-hidden />
        )}
      </div>
      <p className="rtts-public-byline">{STUDIO_COPY.publicWatchByline}</p>
      <a
        className="rtts-public-cta"
        data-testid="rtts-public-cta"
        href={STUDIO_COPY.publicWatchCtaHref}
      >
        {STUDIO_COPY.publicWatchCta}
      </a>
      {reel && !missing ? <PublicWatchHowItWorks /> : null}
    </main>
  );
};

export default PublicReelWatchPage;
